<template>
    <div class="glass-container">
        <div class="zip-customers-container">
            <div class="modern-card">
                <!-- Header -->
                <div class="header-section">
                    <div class="title-section">
                        <h1 class="page-title">Zip Code Analysis</h1>
                        <p class="page-subtitle">Detailed breakdown of customers by zip code</p>
                    </div>
                    
                    <!-- Stats Overview -->
                    <div class="header-stats">
                        <div class="stat-badge">
                            <b-icon icon="map" class="stat-icon"></b-icon>
                            <span class="stat-value">{{ zipCodeData.length }}</span>
                            <span class="stat-label">Zip Codes</span>
                        </div>
                        <div class="stat-badge">
                            <b-icon icon="people" class="stat-icon"></b-icon>
                            <span class="stat-value">{{ totalCustomers.toLocaleString() }}</span>
                            <span class="stat-label">Total Customers</span>
                        </div>
                        <div class="stat-badge">
                            <b-icon icon="cash-stack" class="stat-icon"></b-icon>
                            <span class="stat-value">${{ totalRevenue.toLocaleString() }}</span>
                            <span class="stat-label">Total Revenue</span>
                        </div>
                    </div>
                </div>

                <!-- Filter Controls -->
                <div class="filter-section">
                    <div class="filter-container-glass">
                        <div class="filter-row">
                            <div class="filter-group">
                                <label class="filter-label">
                                    <b-icon icon="funnel" class="mr-2"></b-icon>
                                    Filter by State
                                </label>
                                <b-form-select 
                                    v-model="selectedState" 
                                    :options="stateOptions" 
                                    @change="filterData"
                                    class="glass-select"
                                >
                                    <template #first>
                                        <b-form-select-option value="">
                                            <em>All States</em>
                                        </b-form-select-option>
                                    </template>
                                </b-form-select>
                            </div>
                            
                            <div class="filter-group">
                                <label class="filter-label">
                                    <b-icon icon="search" class="mr-2"></b-icon>
                                    Search Zip Code
                                </label>
                                <b-input-group>
                                    <b-form-input 
                                        v-model="zipSearch" 
                                        placeholder="Enter zip code (e.g., 90210)..." 
                                        @input="filterData"
                                        class="glass-input"
                                    ></b-form-input>
                                    <b-input-group-append v-if="zipSearch">
                                        <b-button 
                                            @click="zipSearch = ''; filterData()" 
                                            variant="outline-secondary"
                                            class="clear-btn"
                                        >
                                            <b-icon icon="x"></b-icon>
                                        </b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </div>
                            
                            <div class="filter-group">
                                <label class="filter-label">
                                    <b-icon icon="sort-down" class="mr-2"></b-icon>
                                    Sort By
                                </label>
                                <b-form-select 
                                    v-model="zipSortBy" 
                                    :options="sortOptions"
                                    @change="applySorting"
                                    class="glass-select"
                                ></b-form-select>
                            </div>
                        </div>
                        
                        <!-- Active Filters -->
                        <div class="active-filters" v-if="hasActiveFilters">
                            <div class="filter-tags">
                                <span 
                                    v-if="selectedState" 
                                    class="filter-tag active"
                                    @click="selectedState = ''; filterData()"
                                >
                                    <b-icon icon="x-circle" class="mr-1"></b-icon>
                                    State: {{ selectedState }}
                                </span>
                                
                                <span 
                                    v-if="zipSearch" 
                                    class="filter-tag active"
                                    @click="zipSearch = ''; filterData()"
                                >
                                    <b-icon icon="x-circle" class="mr-1"></b-icon>
                                    Search: "{{ zipSearch }}"
                                </span>
                                
                                <b-button 
                                    @click="clearAllFilters" 
                                    variant="link" 
                                    size="sm"
                                    class="clear-all-btn"
                                >
                                    <b-icon icon="eraser" class="mr-1"></b-icon>
                                    Clear All
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Zip Code Table -->
                <div class="table-section">
                    <div class="table-container-glass">
                        <div class="table-header-section">
                            <h3 class="table-title">Zip Code Performance</h3>
                            <div class="table-summary">
                                <span class="summary-text">
                                    Showing {{ filteredZipData.length }} of {{ zipCodeData.length }} zip codes
                                </span>
                                <b-button 
                                    @click="exportToCSV" 
                                    variant="outline-primary" 
                                    size="sm"
                                    class="export-btn"
                                >
                                    <b-icon icon="download" class="mr-2"></b-icon>
                                    Export CSV
                                </b-button>
                            </div>
                        </div>
                        
                        <!-- Loading State -->
                        <div v-if="loading" class="loading-container">
                            <div class="spinner-border text-primary" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                            <div class="loading-text">Loading zip code data...</div>
                        </div>
                        
                        <!-- Table Content -->
                        <div v-else>
                            <div v-if="filteredZipData.length > 0" class="table-responsive">
                                <table class="modern-table">
                                    <thead>
                                        <tr>
                                            <th 
                                                v-for="field in zipFields" 
                                                :key="field.key"
                                                class="table-header"
                                                @click="toggleSort(field.key)"
                                            >
                                                <div class="header-content">
                                                    <span>{{ field.label }}</span>
                                                    <span class="sort-indicator" v-if="zipSortBy === field.key">
                                                        {{ zipSortDesc ? '↓' : '↑' }}
                                                    </span>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr 
                                            v-for="(zip, index) in sortedZipData" 
                                            :key="index"
                                            class="table-row"
                                            :class="getZipRowClass(zip)"
                                        >
                                            <td class="table-cell">
                                                <div class="zip-code-cell">
                                                    <div class="zip-badge">
                                                        <b-icon icon="pin-map" class="mr-2"></b-icon>
                                                        {{ zip.zip_code }}
                                                    </div>
                                                    <small class="zip-type" v-if="isPOBox(zip.zip_code)">
                                                        PO Box
                                                    </small>
                                                </div>
                                            </td>
                                            
                                            <td class="table-cell">
                                                <div class="state-cell">
                                                    <b-badge variant="secondary" class="state-badge">
                                                        {{ zip.state }}
                                                    </b-badge>
                                                    <span class="state-name">{{ getStateName(zip.state) }}</span>
                                                </div>
                                            </td>
                                            
                                            <td class="table-cell">
                                                <div class="customer-cell">
                                                    <div class="customer-count">
                                                        <b-icon icon="person" class="mr-2"></b-icon>
                                                        {{ zip.customer_count.toLocaleString() }}
                                                    </div>
                                                    <div class="customer-progress">
                                                        <div class="progress glass-progress">
                                                            <div 
                                                                class="progress-bar" 
                                                                :style="{ width: getCustomerProgress(zip.customer_count) + '%' }"
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            
                                            <td class="table-cell">
                                                <div class="orders-cell">
                                                    <b-badge variant="primary" class="orders-badge">
                                                        {{ zip.total_orders.toLocaleString() }}
                                                    </b-badge>
                                                </div>
                                            </td>
                                            
                                            <td class="table-cell">
                                                <div class="revenue-cell">
                                                    <div class="revenue-amount">
                                                        <b-icon icon="currency-dollar" class="mr-2"></b-icon>
                                                        ${{ zip.total_revenue.toLocaleString() }}
                                                    </div>
                                                    <div class="revenue-trend" v-if="getRevenueTrend(zip)">
                                                        <b-icon 
                                                            :icon="getRevenueTrend(zip).icon" 
                                                            :class="getRevenueTrend(zip).class"
                                                        ></b-icon>
                                                        <small>{{ getRevenueTrend(zip).text }}</small>
                                                    </div>
                                                </div>
                                            </td>
                                            
                                            <td class="table-cell">
                                                <div class="aov-cell">
                                                    <b-badge :variant="getAOVVariant(zip.avg_order_value)" class="aov-badge">
                                                        ${{ zip.avg_order_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                                                    </b-badge>
                                                </div>
                                            </td>
                                            
                                            <td class="table-cell">
                                                <div class="actions-cell">
                                                    <b-button 
                                                        @click="viewZipDetails(zip)" 
                                                        variant="outline-primary" 
                                                        size="sm"
                                                        class="action-btn"
                                                        title="View Details"
                                                    >
                                                        <b-icon icon="eye" class="mr-1"></b-icon>
                                                        Details
                                                    </b-button>
                                                    
                                                    <b-button 
                                                        @click="showOnMap(zip)" 
                                                        variant="outline-info" 
                                                        size="sm"
                                                        class="action-btn"
                                                        title="Show on Map"
                                                    >
                                                        <b-icon icon="map" class="mr-1"></b-icon>
                                                    </b-button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            
                            <!-- Empty State -->
                            <div v-else class="empty-state">
                                <div class="empty-state-icon">
                                    <b-icon icon="map" font-scale="4"></b-icon>
                                </div>
                                <h3>No Zip Codes Found</h3>
                                <p v-if="hasActiveFilters">
                                    No zip codes match your current filters. Try adjusting your search criteria.
                                </p>
                                <p v-else>
                                    No zip code data available. Customer data will appear here once available.
                                </p>
                                <div class="empty-state-actions mt-3">
                                    <b-button 
                                        @click="clearAllFilters" 
                                        variant="outline-secondary" 
                                        v-if="hasActiveFilters"
                                    >
                                        Clear All Filters
                                    </b-button>
                                    <b-button 
                                        @click="loadSampleData" 
                                        variant="outline-primary"
                                        class="ml-2"
                                    >
                                        Load Sample Data
                                    </b-button>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Table Footer -->
                        <div class="table-footer" v-if="filteredZipData.length > 0">
                            <div class="pagination-info">
                                Showing {{ Math.min(filteredZipData.length, 50) }} records
                                <span v-if="filteredZipData.length > 50"> (top 50 by {{ getSortLabel() }})</span>
                            </div>
                            <div class="table-stats">
                                <div class="stat-item">
                                    <span class="stat-label">Avg Customers:</span>
                                    <span class="stat-value">{{ avgCustomers.toLocaleString() }}</span>
                                </div>
                                <div class="stat-item">
                                    <span class="stat-label">Avg Revenue:</span>
                                    <span class="stat-value">${{ avgRevenue.toLocaleString() }}</span>
                                </div>
                                <div class="stat-item">
                                    <span class="stat-label">Top Zip:</span>
                                    <span class="stat-value">{{ topZipCode || 'N/A' }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Zip Details Modal -->
        <b-modal 
            id="zip-details-modal" 
            title="Zip Code Details" 
            size="lg" 
            hide-footer
            class="glass-modal"
        >
            <div class="modal-content-glass">
                <div v-if="selectedZip" class="zip-details">
                    <!-- Header -->
                    <div class="modal-header-section">
                        <div class="modal-title-section">
                            <h3 class="modal-title">
                                <b-icon icon="pin-map" class="mr-3"></b-icon>
                                Zip Code: {{ selectedZip.zip_code }}
                            </h3>
                            <p class="modal-subtitle">
                                {{ getStateName(selectedZip.state) }} ({{ selectedZip.state }})
                            </p>
                        </div>
                        <div class="modal-stats">
                            <div class="modal-stat">
                                <div class="modal-stat-value">{{ selectedZip.customer_count }}</div>
                                <div class="modal-stat-label">Customers</div>
                            </div>
                            <div class="modal-stat">
                                <div class="modal-stat-value">${{ selectedZip.total_revenue.toLocaleString() }}</div>
                                <div class="modal-stat-label">Revenue</div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Details Grid -->
                    <div class="details-grid">
                        <div class="detail-card">
                            <div class="detail-icon">
                                <b-icon icon="people"></b-icon>
                            </div>
                            <div class="detail-content">
                                <div class="detail-label">Total Customers</div>
                                <div class="detail-value">{{ selectedZip.customer_count }}</div>
                            </div>
                        </div>
                        
                        <div class="detail-card">
                            <div class="detail-icon">
                                <b-icon icon="receipt"></b-icon>
                            </div>
                            <div class="detail-content">
                                <div class="detail-label">Total Orders</div>
                                <div class="detail-value">{{ selectedZip.total_orders }}</div>
                            </div>
                        </div>
                        
                        <div class="detail-card">
                            <div class="detail-icon">
                                <b-icon icon="cash-stack"></b-icon>
                            </div>
                            <div class="detail-content">
                                <div class="detail-label">Total Revenue</div>
                                <div class="detail-value">${{ selectedZip.total_revenue.toLocaleString() }}</div>
                            </div>
                        </div>
                        
                        <div class="detail-card">
                            <div class="detail-icon">
                                <b-icon icon="graph-up"></b-icon>
                            </div>
                            <div class="detail-content">
                                <div class="detail-label">Avg Order Value</div>
                                <div class="detail-value">${{ selectedZip.avg_order_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Customer List -->
                    <div class="customer-list-section" v-if="selectedZip.customers && selectedZip.customers.length > 0">
                        <h4 class="section-title">Customers in this Zip Code</h4>
                        <div class="customers-list">
                            <div 
                                v-for="(customer, idx) in selectedZip.customers.slice(0, 5)" 
                                :key="idx"
                                class="customer-item"
                            >
                                <div class="customer-info">
                                    <b-icon icon="person-circle" class="customer-icon"></b-icon>
                                    <div>
                                        <div class="customer-name">{{ customer.customer_name || 'Unknown Customer' }}</div>
                                        <div class="customer-details">
                                            <small class="text-muted">
                                                {{ customer.total_orders || 0 }} orders • 
                                                ${{ (customer.total_spent || 0).toLocaleString() }}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="selectedZip.customers.length > 5" class="more-customers">
                                <small class="text-muted">
                                    + {{ selectedZip.customers.length - 5 }} more customers
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
export default {
    name: 'ZipCodeCustomers',
    props: {
        locationData: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            selectedState: '',
            zipSearch: '',
            zipSortBy: 'customer_count',
            zipSortDesc: true,
            loading: false,
            selectedZip: null,
            zipFields: [
                { key: 'zip_code', label: 'Zip Code', sortable: true },
                { key: 'state', label: 'State', sortable: true },
                { key: 'customer_count', label: 'Customers', sortable: true },
                { key: 'total_orders', label: 'Orders', sortable: true },
                { key: 'total_revenue', label: 'Revenue', sortable: true },
                { key: 'avg_order_value', label: 'Avg Order Value', sortable: true },
                { key: 'actions', label: 'Actions', sortable: false }
            ],
            zipCodeData: [],
            sortOptions: [
                { value: 'customer_count', text: 'Customers (High to Low)' },
                { value: 'total_revenue', text: 'Revenue (High to Low)' },
                { value: 'avg_order_value', text: 'Avg Order (High to Low)' },
                { value: 'total_orders', text: 'Orders (High to Low)' },
                { value: 'zip_code', text: 'Zip Code (A to Z)' },
                { value: 'state', text: 'State (A to Z)' }
            ],
            stateNames: {
                'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
                'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
                'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
                'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
                'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
                'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
                'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
                'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
                'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
                'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
            }
        };
    },
    computed: {
        stateOptions() {
            const states = new Set();
            this.zipCodeData.forEach(item => {
                if (item.state) states.add(item.state);
            });
            return Array.from(states).sort().map(state => ({
                value: state,
                text: state
            }));
        },
        
        filteredZipData() {
            return this.zipCodeData.filter(item => {
                const matchesState = !this.selectedState || item.state === this.selectedState;
                const matchesSearch = !this.zipSearch || 
                    item.zip_code.toString().includes(this.zipSearch);
                return matchesState && matchesSearch;
            });
        },
        
        sortedZipData() {
            let sorted = [...this.filteredZipData];
            
            if (this.zipSortBy === 'customer_count') {
                sorted.sort((a, b) => {
                    return this.zipSortDesc ? b.customer_count - a.customer_count : a.customer_count - b.customer_count;
                });
            } else if (this.zipSortBy === 'total_revenue') {
                sorted.sort((a, b) => {
                    return this.zipSortDesc ? b.total_revenue - a.total_revenue : a.total_revenue - b.total_revenue;
                });
            } else if (this.zipSortBy === 'avg_order_value') {
                sorted.sort((a, b) => {
                    return this.zipSortDesc ? b.avg_order_value - a.avg_order_value : a.avg_order_value - b.avg_order_value;
                });
            } else if (this.zipSortBy === 'total_orders') {
                sorted.sort((a, b) => {
                    return this.zipSortDesc ? b.total_orders - a.total_orders : a.total_orders - b.total_orders;
                });
            } else if (this.zipSortBy === 'state') {
                sorted.sort((a, b) => {
                    if (this.zipSortDesc) {
                        return b.state.localeCompare(a.state);
                    }
                    return a.state.localeCompare(b.state);
                });
            } else {
                // Sort by zip code
                sorted.sort((a, b) => {
                    if (this.zipSortDesc) {
                        return b.zip_code.localeCompare(a.zip_code);
                    }
                    return a.zip_code.localeCompare(b.zip_code);
                });
            }
            
            // Return top 50 for performance
            return sorted.slice(0, 50);
        },
        
        hasActiveFilters() {
            return this.selectedState || this.zipSearch;
        },
        
        totalCustomers() {
            return this.zipCodeData.reduce((sum, zip) => sum + zip.customer_count, 0);
        },
        
        totalRevenue() {
            return this.zipCodeData.reduce((sum, zip) => sum + zip.total_revenue, 0);
        },
        
        maxCustomers() {
            if (this.zipCodeData.length === 0) return 1;
            return Math.max(...this.zipCodeData.map(z => z.customer_count), 1);
        },
        
        avgCustomers() {
            if (this.zipCodeData.length === 0) return 0;
            return Math.round(this.totalCustomers / this.zipCodeData.length);
        },
        
        avgRevenue() {
            if (this.zipCodeData.length === 0) return 0;
            return Math.round(this.totalRevenue / this.zipCodeData.length);
        },
        
        topZipCode() {
            if (this.sortedZipData.length === 0) return null;
            return this.sortedZipData[0].zip_code;
        }
    },
    created() {
        console.log('ZipCodeCustomers component created');
        this.processZipCodeData();
    },
    watch: {
        locationData: {
            immediate: true,
            handler(newData) {
                console.log('Location data changed:', newData);
                if (newData && newData.length > 0) {
                    this.processZipCodeData();
                }
            }
        }
    },
    methods: {
        processZipCodeData() {
            this.loading = true;
            console.log('Processing zip code data...');
            console.log('Raw location data:', this.locationData);
            
            setTimeout(() => {
                const zipMap = new Map();
                
                if (!this.locationData || this.locationData.length === 0) {
                    console.warn('No location data provided to process');
                    this.zipCodeData = [];
                    this.loading = false;
                    return;
                }
                
                // Process all location data
                this.locationData.forEach(location => {
                    console.log('Processing location:', location);
                    
                    // Process each customer in the location
                    if (location.customers && Array.isArray(location.customers)) {
                        console.log(`Found ${location.customers.length} customers in location`);
                        
                        location.customers.forEach(customer => {
                            console.log('Processing customer:', customer);
                            
                            let zipCode = null;
                            let state = location.state || 'Unknown';
                            
                            // Try to extract zip code from customer address
                            if (customer.customer_address) {
                                console.log('Customer address:', customer.customer_address);
                                
                                // Try regex for 5-digit zip code
                                const zipMatch = customer.customer_address.match(/\b\d{5}\b/);
                                if (zipMatch) {
                                    zipCode = zipMatch[0];
                                    console.log('Found zip code in address:', zipCode);
                                }
                                
                                // Try to extract state from address
                                const stateMatch = customer.customer_address.match(/,\s*([A-Z]{2})\s*(?:,|\d|$)/);
                                if (stateMatch) {
                                    state = stateMatch[1];
                                    console.log('Found state in address:', state);
                                }
                            }
                            
                            // If no zip found in address, check other customer fields
                            if (!zipCode && customer.zipcode) {
                                const zipMatch = customer.zipcode.toString().match(/\d{5}/);
                                if (zipMatch) {
                                    zipCode = zipMatch[0];
                                    console.log('Found zip code in zipcode field:', zipCode);
                                }
                            }
                            
                            if (zipCode) {
                                const zipKey = zipCode + '|' + state;
                                console.log('Zip key:', zipKey);
                                
                                if (!zipMap.has(zipKey)) {
                                    zipMap.set(zipKey, {
                                        zip_code: zipCode,
                                        state: state,
                                        customer_count: 0,
                                        total_orders: 0,
                                        total_revenue: 0,
                                        customers: []
                                    });
                                }
                                
                                const zipData = zipMap.get(zipKey);
                                zipData.customer_count++;
                                zipData.total_orders += customer.total_orders || 1;
                                zipData.total_revenue += customer.total_spent || 0;
                                zipData.customers.push(customer);
                                
                                console.log('Updated zip data for ' + zipCode + ':', {
                                    customers: zipData.customer_count,
                                    orders: zipData.total_orders,
                                    revenue: zipData.total_revenue
                                });
                            } else {
                                console.log('No zip code found for customer:', customer);
                            }
                        });
                    }
                    
                    // Also process location-level data if available
                    if (location.customer_zipcodes && Array.isArray(location.customer_zipcodes)) {
                        console.log('Processing location zip codes:', location.customer_zipcodes);
                        
                        location.customer_zipcodes.forEach(zipCode => {
                            if (zipCode && zipCode.toString().match(/^\d{5}$/)) {
                                const zipKey = zipCode + '|' + (location.state || 'Unknown');
                                
                                if (!zipMap.has(zipKey)) {
                                    zipMap.set(zipKey, {
                                        zip_code: zipCode,
                                        state: location.state || 'Unknown',
                                        customer_count: location.customer_count || 0,
                                        total_orders: location.total_orders || 0,
                                        total_revenue: location.total_revenue || 0,
                                        customers: location.customers || []
                                    });
                                } else {
                                    const zipData = zipMap.get(zipKey);
                                    zipData.customer_count += location.customer_count || 0;
                                    zipData.total_orders += location.total_orders || 0;
                                    zipData.total_revenue += location.total_revenue || 0;
                                }
                            }
                        });
                    }
                });
                
                // Calculate averages and format
                this.zipCodeData = Array.from(zipMap.values()).map(zipData => ({
                    ...zipData,
                    avg_order_value: zipData.total_orders > 0 ? 
                        zipData.total_revenue / zipData.total_orders : 0
                }));
                
                console.log('Final processed zip code data:', this.zipCodeData);
                console.log('Found ' + this.zipCodeData.length + ' unique zip codes');
                
                // If still no data, load sample data
                if (this.zipCodeData.length === 0) {
                    console.log('No data found. You can use sample data for testing.');
                }
                
                this.loading = false;
            }, 800);
        },
        
        filterData() {
            // Data is filtered in computed property
            console.log('Filtering data. Selected state:', this.selectedState, 'Search:', this.zipSearch);
        },
        
        toggleSort(field) {
            if (this.zipSortBy === field) {
                this.zipSortDesc = !this.zipSortDesc;
            } else {
                this.zipSortBy = field;
                this.zipSortDesc = true;
            }
            console.log('Sorting by:', field, 'Descending:', this.zipSortDesc);
        },
        
        applySorting() {
            console.log('Applying sort:', this.zipSortBy);
        },
        
        clearAllFilters() {
            this.selectedState = '';
            this.zipSearch = '';
            this.zipSortBy = 'customer_count';
            this.zipSortDesc = true;
            console.log('All filters cleared');
        },
        
        viewZipDetails(zipData) {
            this.selectedZip = zipData;
            console.log('Viewing details for zip:', zipData.zip_code);
            this.$bvModal.show('zip-details-modal');
        },
        
        showOnMap(zipData) {
            // Emit event to parent component to show zip on map
            this.$emit('show-on-map', zipData);
            this.$bvToast.toast('Showing ' + zipData.zip_code + ' on map', {
                title: 'Map View',
                variant: 'info',
                solid: true,
                autoHideDelay: 2000
            });
        },
        
        getCustomerProgress(customerCount) {
            if (!customerCount) return 0;
            const progress = Math.min(100, (customerCount / this.maxCustomers) * 100);
            return Math.round(progress);
        },
        
        getAOVVariant(aov) {
            if (aov > 2000) return 'success';
            if (aov > 1000) return 'warning';
            return 'danger';
        },
        
        getZipRowClass(zip) {
            if (zip.customer_count > 100) return 'row-high-customers';
            if (zip.customer_count > 50) return 'row-medium-customers';
            return 'row-low-customers';
        },
        
        getStateName(stateCode) {
            return this.stateNames[stateCode] || stateCode;
        },
        
        getRevenueTrend(zip) {
            const aov = zip.avg_order_value;
            if (aov > 2000) {
                return {
                    icon: 'arrow-up',
                    class: 'text-success',
                    text: 'High AOV'
                };
            } else if (aov > 1000) {
                return {
                    icon: 'arrow-right',
                    class: 'text-warning',
                    text: 'Medium AOV'
                };
            } else {
                return {
                    icon: 'arrow-down',
                    class: 'text-danger',
                    text: 'Low AOV'
                };
            }
        },
        
        isPOBox(zipCode) {
            // Simple heuristic for PO Box zip codes (some PO Box areas start with specific digits)
            const poBoxPrefixes = ['900', '901', '902', '903', '904', '905'];
            const prefix = zipCode.toString().substring(0, 3);
            return poBoxPrefixes.includes(prefix);
        },
        
        getSortLabel() {
            const option = this.sortOptions.find(opt => opt.value === this.zipSortBy);
            return option ? option.text.split('(')[0].trim() : 'Customers';
        },
        
        exportToCSV() {
            if (this.zipCodeData.length === 0) {
                this.$bvToast.toast('No data to export', {
                    title: 'Export Error',
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 2000
                });
                return;
            }
            
            const data = this.sortedZipData.map(zip => ({
                'Zip Code': zip.zip_code,
                'State': zip.state,
                'State Name': this.getStateName(zip.state),
                'Customers': zip.customer_count,
                'Total Orders': zip.total_orders,
                'Total Revenue': zip.total_revenue,
                'Average Order Value': zip.avg_order_value.toFixed(2)
            }));
            
            const csv = this.convertToCSV(data);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            
            link.setAttribute('href', url);
            link.setAttribute('download', 'zip-code-analysis-' + new Date().toISOString().split('T')[0] + '.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            this.$bvToast.toast('Zip code data exported successfully', {
                title: 'Export Complete',
                variant: 'success',
                solid: true,
                autoHideDelay: 2000
            });
        },
        
        convertToCSV(objArray) {
            const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
            let str = '';
            
            // Add headers
            const headers = Object.keys(array[0]);
            str += headers.join(',') + '\r\n';
            
            // Add data rows
            for (let i = 0; i < array.length; i++) {
                let line = '';
                headers.forEach(header => {
                    if (line !== '') line += ',';
                    line += array[i][header];
                });
                str += line + '\r\n';
            }
            return str;
        },
        
        loadSampleData() {
            console.log('Loading sample data for testing...');
            this.zipCodeData = [
                {
                    zip_code: '19134',
                    state: 'PA',
                    customer_count: 2,
                    total_orders: 6,
                    total_revenue: 6056.20,
                    avg_order_value: 1009.37,
                    customers: [
                        {
                            customer_name: 'Test Customer 1',
                            customer_email: 'test1@example.com',
                            total_orders: 4,
                            total_spent: 2379.68,
                            customer_address: '123 Main St, Philadelphia, PA, 19134'
                        },
                        {
                            customer_name: 'Test Customer 2',
                            customer_email: 'test2@example.com',
                            total_orders: 2,
                            total_spent: 3676.52,
                            customer_address: '456 Oak Ave, Philadelphia, PA, 19134'
                        }
                    ]
                },
                {
                    zip_code: '19101',
                    state: 'PA',
                    customer_count: 1,
                    total_orders: 1,
                    total_revenue: 1843.08,
                    avg_order_value: 1843.08,
                    customers: [
                        {
                            customer_name: 'Test Customer 3',
                            customer_email: 'test3@example.com',
                            total_orders: 1,
                            total_spent: 1843.08,
                            customer_address: '789 Pine St, Philadelphia, PA, 19101'
                        }
                    ]
                },
                {
                    zip_code: '08632',
                    state: 'NJ',
                    customer_count: 1,
                    total_orders: 3,
                    total_revenue: 2850.00,
                    avg_order_value: 950.00,
                    customers: [
                        {
                            customer_name: 'Test Customer 4',
                            customer_email: 'test4@example.com',
                            total_orders: 3,
                            total_spent: 2850.00,
                            customer_address: '101 Maple Rd, Trenton, NJ, 08632'
                        }
                    ]
                }
            ];
            
            this.$bvToast.toast('Sample data loaded for demonstration', {
                title: 'Sample Data',
                variant: 'info',
                solid: true,
                autoHideDelay: 2000
            });
        }
    }
}
</script>

<style scoped>
.glass-container {
    min-height: 100vh;
    padding: 20px;
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    z-index: 0;
}

.zip-customers-container {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    border-radius: 34px;
}

.modern-card {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border-radius: 24px;
    box-shadow: 
        0 8px 32px rgba(31, 38, 135, 0.2),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    padding: 30px;
    margin-top: 20px;
    animation: cardAppear 0.6s ease-out;
    position: relative;
    overflow: hidden;
}

.modern-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(255, 255, 255, 0.8) 50%, 
        transparent 100%);
}

/* Header Section */
.header-section {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
    position: relative;
}

.header-section::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    border-radius: 2px;
    animation: slideIn 0.6s ease-out;
}

.title-section {
    animation: slideInUp 0.5s ease-out;
}

.page-title {
    font-size: 2.2rem;
    font-weight: 700;
    background: linear-gradient(135deg, #364050 0%, #364050 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 8px;
    text-shadow: 0 2px 10px rgba(102, 126, 234, 0.1);
}

.page-subtitle {
    color: rgba(108, 117, 125, 0.9);
    font-size: 1rem;
    font-weight: 400;
}

.header-stats {
    display: flex;
    gap: 20px;
    margin-top: 20px;
    animation: slideInDown 0.5s ease-out;
}

.stat-badge {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 12px;
    padding: 15px;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: all 0.3s ease;
    min-width: 180px;
}

.stat-badge:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
    border-color: #667eea;
}

.stat-icon {
    color: #667eea;
    font-size: 1.5rem;
}

.stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2d3748;
}

.stat-label {
    font-size: 0.85rem;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Filter Section */
.filter-section {
    margin-bottom: 30px;
    animation: fadeIn 0.6s ease-out 0.2s both;
}

.filter-container-glass {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 25px;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
}

.filter-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.filter-group {
    flex: 1;
    min-width: 250px;
}

.filter-label {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-weight: 600;
    color: #4a5568;
    font-size: 0.9rem;
}

.glass-select, .glass-input {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 8px;
    color: #4a5568;
    transition: all 0.3s ease;
}

.glass-select:focus, .glass-input:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.clear-btn {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-left: none;
    border-radius: 0 8px 8px 0;
    color: #718096;
}

.clear-btn:hover {
    background: rgba(245, 101, 101, 0.1);
    color: #e53e3e;
    border-color: rgba(229, 62, 62, 0.3);
}

.active-filters {
    padding-top: 20px;
    border-top: 1px solid rgba(226, 232, 240, 0.3);
}

.filter-tags {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
}

.filter-tag {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
    padding: 6px 15px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
    border: 1px solid rgba(102, 126, 234, 0.2);
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
}

.filter-tag:hover {
    background: rgba(102, 126, 234, 0.2);
    transform: translateY(-2px);
}

.clear-all-btn {
    color: #718096;
    text-decoration: none;
    margin-left: auto;
}

.clear-all-btn:hover {
    color: #e53e3e;
    text-decoration: none;
}

/* Table Section */
.table-section {
    animation: fadeIn 0.6s ease-out 0.4s both;
}

.table-container-glass {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
}

.table-header-section {
    padding: 20px 25px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.table-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.table-summary {
    display: flex;
    align-items: center;
    gap: 20px;
}

.summary-text {
    color: #718096;
    font-size: 0.9rem;
    font-weight: 500;
}

.export-btn {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(102, 126, 234, 0.3);
    color: #667eea;
    border-radius: 10px;
    padding: 6px 16px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.export-btn:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
}

/* Loading Container */
.loading-container {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    padding: 60px 40px;
    text-align: center;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    animation: pulse 2s infinite;
}

.loading-text {
    color: #4a5568;
    margin-top: 15px;
    font-weight: 500;
}

/* Modern Table */
.modern-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.table-header {
    background: linear-gradient(135deg, 
        rgba(102, 126, 234, 0.1) 0%, 
        rgba(118, 75, 162, 0.1) 100%);
    padding: 15px 20px;
    text-align: left;
    font-weight: 600;
    color: #4a5568;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid rgba(226, 232, 240, 0.5);
    position: relative;
    cursor: pointer;
    user-select: none;
    transition: all 0.3s ease;
}

.table-header:hover {
    background: linear-gradient(135deg, 
        rgba(102, 126, 234, 0.15) 0%, 
        rgba(118, 75, 162, 0.15) 100%);
}

.table-header::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    transition: width 0.3s ease;
}

.table-header:hover::after {
    width: 100%;
}

.header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.sort-indicator {
    margin-left: 8px;
    font-weight: bold;
    color: #667eea;
}

.table-row {
    border-bottom: 1px solid rgba(226, 232, 240, 0.3);
    transition: all 0.3s ease;
    position: relative;
}

.table-row:hover {
    background: rgba(102, 126, 234, 0.05);
    transform: translateX(4px);
}

.table-row.row-high-customers {
    background: linear-gradient(90deg, rgba(56, 161, 105, 0.05), transparent);
}

.table-row.row-medium-customers {
    background: linear-gradient(90deg, rgba(237, 137, 54, 0.05), transparent);
}

.table-row.row-low-customers {
    background: transparent;
}

.table-cell {
    padding: 15px 20px;
    vertical-align: middle;
    color: #4a5568;
    font-size: 0.95rem;
}

/* Zip Code Cell */
.zip-code-cell {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.zip-badge {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 6px 12px;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.9rem;
    display: inline-flex;
    align-items: center;
    width: fit-content;
}

.zip-type {
    color: #718096;
    font-size: 0.75rem;
    font-style: italic;
}

/* State Cell */
.state-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}

.state-badge {
    background: rgba(160, 174, 192, 0.8);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    padding: 4px 10px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.8rem;
    min-width: 40px;
    text-align: center;
}

.state-name {
    color: #718096;
    font-size: 0.85rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Customer Cell */
.customer-cell {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.customer-count {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #2d3748;
}

.glass-progress {
    height: 6px;
    border-radius: 3px;
    background: rgba(226, 232, 240, 0.5);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    overflow: hidden;
}

.glass-progress .progress-bar {
    background: linear-gradient(90deg, 
        #4299e1 0%, 
        #3182ce 100%);
    border-radius: 3px;
    position: relative;
    overflow: hidden;
    transition: width 1s ease;
}

.glass-progress .progress-bar::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(255, 255, 255, 0.3) 50%, 
        transparent 100%);
    animation: shimmer 2s infinite;
}

/* Orders Cell */
.orders-badge {
    background: linear-gradient(135deg, 
        rgba(159, 122, 234, 0.8) 0%, 
        rgba(128, 90, 213, 0.7) 100%);
    color: white;
    border-color: transparent;
    padding: 6px 12px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.85rem;
}

/* Revenue Cell */
.revenue-cell {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.revenue-amount {
    display: flex;
    align-items: center;
    font-weight: 700;
    color: #276749;
    font-size: 1rem;
}

.revenue-trend {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.8rem;
}

/* AOV Cell */
.aov-badge {
    padding: 6px 12px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.85rem;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    min-width: 90px;
    text-align: center;
}

.aov-badge.badge-success {
    background: linear-gradient(135deg, 
        rgba(56, 161, 105, 0.8) 0%, 
        rgba(56, 161, 105, 0.7) 100%);
    color: white;
    border-color: transparent;
}

.aov-badge.badge-warning {
    background: linear-gradient(135deg, 
        rgba(237, 137, 54, 0.8) 0%, 
        rgba(221, 107, 32, 0.7) 100%);
    color: white;
    border-color: transparent;
}

.aov-badge.badge-danger {
    background: linear-gradient(135deg, 
        rgba(245, 101, 101, 0.8) 0%, 
        rgba(229, 62, 62, 0.7) 100%);
    color: white;
    border-color: transparent;
}

/* Actions Cell */
.actions-cell {
    display: flex;
    gap: 8px;
}

.action-btn {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 0.85rem;
    transition: all 0.3s ease;
}

.action-btn:first-child {
    color: #667eea;
    border-color: rgba(102, 126, 234, 0.3);
}

.action-btn:first-child:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: transparent;
}

.action-btn:last-child {
    color: #4299e1;
    border-color: rgba(66, 153, 225, 0.3);
}

.action-btn:last-child:hover {
    background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
    color: white;
    border-color: transparent;
}

/* Empty State */
.empty-state {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    padding: 60px 40px;
    text-align: center;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 0 0 1px rgba(255, 255, 255, 0.3);
}

.empty-state-icon {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 4rem;
    margin-bottom: 20px;
}

.empty-state h3 {
    color: #2d3748;
    margin-bottom: 10px;
    font-weight: 600;
}

.empty-state p {
    color: #718096;
    margin-bottom: 25px;
    font-size: 1rem;
}

.empty-state-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
}

/* Table Footer */
.table-footer {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    padding: 20px 25px;
    border-top: 1px solid rgba(226, 232, 240, 0.5);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.pagination-info {
    color: #718096;
    font-size: 0.9rem;
    font-weight: 500;
}

.table-stats {
    display: flex;
    gap: 20px;
}

.stat-item {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.stat-label {
    color: #718096;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.stat-value {
    color: #2d3748;
    font-weight: 600;
    font-size: 1rem;
}

/* Modal Styles */
.glass-modal :deep(.modal-content) {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-content-glass {
    padding: 0;
}

.zip-details {
    padding: 0;
}

.modal-header-section {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 25px;
    color: white;
    border-radius: 20px 20px 0 0;
}

.modal-title-section {
    margin-bottom: 20px;
}

.modal-title {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 5px;
    display: flex;
    align-items: center;
}

.modal-subtitle {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1rem;
    margin: 0;
}

.modal-stats {
    display: flex;
    gap: 30px;
}

.modal-stat {
    text-align: center;
}

.modal-stat-value {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 5px;
}

.modal-stat-label {
    font-size: 0.9rem;
    opacity: 0.9;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.details-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    padding: 25px;
}

.detail-card {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 15px;
    transition: all 0.3s ease;
}

.detail-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.detail-icon {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    width: 50px;
    height: 50px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
}

.detail-content {
    flex: 1;
}

.detail-label {
    color: #718096;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 5px;
}

.detail-value {
    color: #2d3748;
    font-size: 1.5rem;
    font-weight: 700;
}

.customer-list-section {
    padding: 0 25px 25px;
}

.section-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.customers-list {
    max-height: 300px;
    overflow-y: auto;
}

.customer-item {
    padding: 15px;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.5);
    border-radius: 10px;
    margin-bottom: 10px;
    transition: all 0.3s ease;
}

.customer-item:hover {
    background: rgba(102, 126, 234, 0.05);
    transform: translateX(4px);
}

.customer-info {
    display: flex;
    align-items: center;
    gap: 15px;
}

.customer-icon {
    color: #667eea;
    font-size: 2rem;
}

.customer-name {
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 5px;
}

.more-customers {
    text-align: center;
    padding: 15px;
    color: #718096;
    font-style: italic;
}

/* Animation Keyframes */
@keyframes cardAppear {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes slideInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideInDown {
    from {
        opacity: 0;
        transform: translateY(-30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideIn {
    from {
        width: 0;
    }
    to {
        width: 60px;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

@keyframes pulse {
    0%, 100% {
        box-shadow: 
            0 4px 20px rgba(0, 0, 0, 0.05),
            inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    }
    50% {
        box-shadow: 
            0 4px 20px rgba(102, 126, 234, 0.15),
            inset 0 0 0 1px rgba(102, 126, 234, 0.1);
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .glass-container {
        padding: 10px;
    }
    
    .modern-card {
        padding: 20px;
        border-radius: 20px;
        margin-top: 10px;
    }
    
    .page-title {
        font-size: 1.8rem;
    }
    
    .header-stats {
        flex-direction: column;
    }
    
    .stat-badge {
        width: 100%;
        min-width: auto;
    }
    
    .filter-row {
        flex-direction: column;
        gap: 15px;
    }
    
    .filter-group {
        min-width: 100%;
    }
    
    .table-header-section {
        flex-direction: column;
        align-items: flex-start;
    }
    
    .table-summary {
        width: 100%;
        justify-content: space-between;
    }
    
    .actions-cell {
        flex-direction: column;
        gap: 5px;
    }
    
    .action-btn {
        width: 100%;
        justify-content: center;
    }
    
    .table-footer {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .table-stats {
        width: 100%;
        justify-content: space-between;
    }
    
    .details-grid {
        grid-template-columns: 1fr;
    }
    
    .modal-stats {
        flex-direction: column;
        gap: 15px;
    }
}

/* Scrollbar Styling */
::-webkit-scrollbar {
    width: 10px;
}

::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 5px;
}

::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}
</style>


// WEBPACK FOOTER //
// src/components/Customers/ZipCodeCustomers.vue